import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo-small.webp";

interface BrandProps {
  size?: "sm" | "md" | "lg";
  showTagline?: boolean;
  linkToHome?: boolean;
  className?: string;
}

const sizeConfig = {
  sm: {
    logo: "h-10 w-10",
    name: "text-base",
    tagline: "text-[9px]",
    gap: "gap-2",
  },
  md: {
    logo: "h-12 w-12",
    name: "text-xl",
    tagline: "text-[10px]",
    gap: "gap-3",
  },
  lg: {
    logo: "h-16 w-16",
    name: "text-2xl",
    tagline: "text-xs",
    gap: "gap-4",
  },
};

export function Brand({ 
  size = "md", 
  showTagline = true, 
  linkToHome = true,
  className 
}: BrandProps) {
  const config = sizeConfig[size];
  
  const content = (
    <div className={cn("flex items-center group", config.gap, className)}>
      {/* Logo */}
      <div className="relative flex-shrink-0">
        <img 
          src={logo} 
          alt="Lineroid Logo" 
          width={size === 'sm' ? 40 : size === 'lg' ? 64 : 48}
          height={size === 'sm' ? 40 : size === 'lg' ? 64 : 48}
          decoding="async"
          className={cn(
            "object-contain",
            config.logo
          )} 
        />
      </div>
      
      {/* Text */}
      <div className="flex flex-col">
        <span className={cn(
          "font-brand font-semibold tracking-wide text-foreground leading-none",
          config.name
        )}>
          LINEROID
        </span>
        {showTagline && (
          <span className={cn(
            "font-mono text-muted-foreground tracking-widest uppercase",
            config.tagline
          )}>
            Humanoid Intelligence
          </span>
        )}
      </div>
    </div>
  );

  if (linkToHome) {
    return (
      <Link to="/" className="inline-flex">
        {content}
      </Link>
    );
  }

  return content;
}

// Compact version for tight spaces (icon only with hover text)
export function BrandIcon({ className }: { className?: string }) {
  return (
    <Link to="/" className={cn("relative group", className)}>
      <img 
        src={logo} 
        alt="Lineroid" 
        width={40}
        height={40}
        decoding="async"
        className="h-10 w-10 object-contain" 
      />
    </Link>
  );
}

// Text-only version for inline use
export function BrandText({ 
  size = "md",
  className 
}: { 
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  const textSizes = {
    sm: "text-base",
    md: "text-xl",
    lg: "text-2xl",
  };

  return (
    <span className={cn(
      "font-brand font-semibold tracking-wide text-foreground",
      textSizes[size],
      className
    )}>
      LINEROID
    </span>
  );
}
